// Actual domain logic should be in a domainLogic.js file, at the appropriate
// level in the hierarchy.
import { IN_TRANSACTION } from "appConfig";

import { removeLotAndSerialValues } from "services/sosInventoryService/domainLogic";
import { getEmptyRecord } from "services/sosInventoryService/returnToVendor/schema";
import { getRecord, getRecordFrom } from "services/sosInventoryService/sosApi";
import { fetchOutboundBinsForLines } from "services/utility/bins";
import {
  copyCustomFieldValues,
  reconcileCustomFields,
  getCustomFieldDefinitions,
} from "services/utility/customFields";
import { getRelatedReferenceObjects } from "services/utility/edit";
import { handleProgramError } from "services/utility/errors";

import globalState from "globalState/globalState";
import { linesLoadingOn, linesLoadingOff } from "globalState/loadingSlice";

import { OBJECT_TYPES } from "appConstants";
import { EMPTY_LINE_ITEM } from "editConfig";

const OBJECT_TYPE = OBJECT_TYPES.RETURN_TO_VENDOR.fullString;

export async function getTransaction(
  id,
  newFromId,
  newFromObjectType,
  updaters,
  customFieldDefs
) {
  let record, lines, relatedRecords;

  if (id || newFromObjectType) {
    if (id) {
      record = await getRecord(OBJECT_TYPE, id);
    } else {
      // it's a "populate from" request
      record = await getRecordFrom(OBJECT_TYPE, newFromObjectType, newFromId);
      delete record.id;
    }

    record.customFields = reconcileCustomFields(
      customFieldDefs,
      record.customFields
    );

    lines = record.lines;
    delete record.lines;

    // render lines immediately while async fields are computed in the background
    globalState.dispatch(linesLoadingOn());
    updaters.lineHandler({ type: "set", lines });
    updaters.setRecord(record);

    relatedRecords = await getRelatedReferenceObjects(OBJECT_TYPE, record);
    updaters.setRelatedRecords(relatedRecords);

    if (relatedRecords.location.binTracking) {
      // set the availableBins for the bin select
      lines = await fetchOutboundBinsForLines(
        lines,
        record.location?.id,
        record.date,
        true // maintainBins - maintain the selected bin when loading the record
      );
    }

    // patch only async-computed fields into the already-rendered lines
    updaters.lineHandler({
      type: "updateFields",
      lines,
      fields: ["availableBins", "bin"],
    });
    globalState.dispatch(linesLoadingOff());
  } else {
    // new record
    const { settings } = globalState.getState().userCompanySettings;
    const defaultLocation =
      globalState.getState().userCompanySettings.settings.defaultLocation;
    const numLinesToAdd =
      globalState.getState().userCompanySettings.settings.numLinesToAdd;
    const defaultLocationForPurchasing =
      globalState.getState().userCompanySettings.settings
        .defaultLocationForPurchasing;

    record = {
      ...getEmptyRecord(settings),
      customFields: reconcileCustomFields(customFieldDefs, []),
    };

    lines = Array(numLinesToAdd).fill(EMPTY_LINE_ITEM[OBJECT_TYPE]);

    relatedRecords = {};

    if (defaultLocation) {
      record.location = defaultLocationForPurchasing || defaultLocation || null;

      const location = await getRecord(
        "location",
        record.location.id,
        IN_TRANSACTION
      );

      relatedRecords = { ...relatedRecords, location };
    }
    updaters.setRelatedRecords(relatedRecords);
    updaters.setRecord(record);
    updaters.lineHandler({ type: "set", lines });
  }
}

export async function updateTransaction(
  field,
  newValue,
  transactionCustomFieldDefs,
  currentState,
  updaters,
  currencies
) {
  const { record, lines, relatedRecords } = currentState;
  if (!record || !lines || !relatedRecords) {
    handleProgramError(new Error("update Transaction | invalid currentState"));
  }

  let newRecord, newRelatedRecords;

  switch (field) {
    case "date":
      newRecord = { ...record, date: newValue };
      updaters.setRecord(newRecord);

      if (newValue && relatedRecords?.location?.binTracking) {
        globalState.dispatch(linesLoadingOn());
        try {
          // set the availableBins for the bin select
          const updatedLines = await fetchOutboundBinsForLines(
            lines,
            record.location?.id,
            newValue
          );
          updaters.lineHandler({
            type: "updateFields",
            lines: updatedLines,
            fields: ["availableBins", "bin"],
          });
        } catch (e) {
          handleProgramError(e);
        } finally {
          globalState.dispatch(linesLoadingOff());
        }
      }
      break;

    case "location":
      globalState.dispatch(linesLoadingOn());
      try {
        newRecord = { ...record, location: newValue };
        updaters.setRecord(newRecord);
        const location = newValue
          ? await getRecord("location", newValue.id)
          : null;
        newRelatedRecords = { ...relatedRecords, location };
        if (location?.binTracking) {
          // set the availableBins for the bin select
          let updatedLines = await fetchOutboundBinsForLines(
            lines,
            newValue?.id,
            record.date
          );
          updatedLines = removeLotAndSerialValues(updatedLines);
          updaters.lineHandler({
            type: "updateFields",
            lines: updatedLines,
            fields: ["availableBins", "bin", "lot", "serials"],
          });
        }
      } catch (e) {
        handleProgramError(e);
      } finally {
        globalState.dispatch(linesLoadingOff());
      }

      break;

    case "account":
    case "comment":
    case "createVendorCredit":
    case "customFields":
    case "department":
    case "number":
    case "shippingMethod":
    case "trackingNumber":
    case "vendorMessage":
    case "vendorNotes":
      newRecord = { ...record, [field]: newValue };
      updaters.setRecord(newRecord);

      break;

    case "vendor":
      // if the field is empty, null the field
      if (!newValue) {
        newRecord = { ...record, vendor: null };
        updaters.setRecord(newRecord);
        break;
      }

      const vendorCustomFieldDefs = await getCustomFieldDefinitions("vendor");

      try {
        // get the newly selected vendor record, so that we can...
        const vendor = await getRecord("vendor", newValue.id, IN_TRANSACTION);

        // be sure there are custom field entries for each defined custom field
        // in the vendor record...
        const vendorCustomFields = reconcileCustomFields(
          vendorCustomFieldDefs,
          vendor.customFields
        );

        const newTransactionCustomFields = copyCustomFieldValues(
          vendorCustomFieldDefs,
          vendorCustomFields,
          transactionCustomFieldDefs,
          record.customFields
        );

        // ...then initialize any transaction custom fields to their matching vendor
        // custom field values, if any

        newRecord = {
          ...record,
          vendor: newValue,
          vendorNotes: vendor.notes,
          taxCode: vendor.taxCode,
          terms: vendor.terms,
          customFields: newTransactionCustomFields,
        };
        updaters.setRecord(newRecord);
      } catch (e) {
        handleProgramError(e);
      }

      break;

    case "currency":
      const { id: newId, name: newName } = newValue || {};
      updaters.setRecord((prev) => ({
        ...prev,
        currency: newId ? { id: newId, name: newName } : null,
        exchangeRate: newId
          ? currencies.find(({ id }) => id === newId).exchangeRate
          : null,
      }));
      break;

    default:
      handleProgramError(
        new Error(`updateTransaction | unknown field (${field})`)
      );
  }

  if (newRelatedRecords) {
    updaters.setRelatedRecords(newRelatedRecords);
  }
}
